import {ref, computed, readonly} from 'vue';
import api from '@/api/api';
import {toast} from '@/components/utils/ui_v4/alerts/toast/toast';
import {API_ENDPOINTS, ERROR_MESSAGES} from '../constants';
import {formatDate, formatDateTime} from '../utils/dateUtils';

// Estado compartilhado
const cotacoes = ref([]);
const cotacaoAtual = ref(null);
const isLoading = ref(false);
const scrollInfo = ref({
  offset: 0,
  limit: 300,
  total: null,
  loaded: null,
  has_more: false,
  loading: false,
  next_offset: 0
});

// Cache para evitar requisições duplicadas
const cache = new Map();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutos

export default function useCotacaoStore() {
  const fetchCotacoes = async (isLoadMore = false) => {
    if (isLoadMore && !scrollInfo.value.has_more) return;

    const offset = isLoadMore ? scrollInfo.value.next_offset : 0;
    const cacheKey = `cotacoes_${offset}_${scrollInfo.value.limit}`;

    // Verificar cache
    if (cache.has(cacheKey)) {
      const cached = cache.get(cacheKey);
      if (Date.now() - cached.timestamp < CACHE_TTL) {
        if (!isLoadMore) {
          cotacoes.value = cached.data;
        }
        return;
      }
    }

    isLoading.value = true;
    scrollInfo.value.loading = true;

    try {
      const {data} = await api.get(API_ENDPOINTS.LISTAR_COTACOES, {
        params: {
          limit: scrollInfo.value.limit,
          offset: offset
        }
      });

      const newItems = processarCotacoes(data.cotacoes || []);

      if (isLoadMore) {
        cotacoes.value = [...cotacoes.value, ...newItems];
      } else {
        cotacoes.value = newItems;
        // Atualizar cache
        cache.set(cacheKey, {
          data: newItems,
          timestamp: Date.now()
        });
      }

      if (data.scroll) {
        scrollInfo.value = {
          ...scrollInfo.value,
          ...data.scroll,
          loading: false
        };
      }
    } catch (error) {
      
      toast.error(ERROR_MESSAGES.LOAD_COTACOES);
    } finally {
      isLoading.value = false;
      scrollInfo.value.loading = false;
    }
  };

  const processarCotacoes = (rawCotacoes) => {
    return rawCotacoes.map(item => {
      const config = item.config && item.config.length > 0 ? item.config[0] : {};

      return {
        ...item.cotacao,
        razao_social: config.razao_social || '',
        idfilial: config.idfilial || '',
        nome_completo: item.responsavel ? item.responsavel.nome_completo : '',
        inicio_periodo_recebimento_proposta: formatDate(config.inicio_periodo_recebimento_proposta),
        termino_periodo_recebimento_proposta: formatDate(config.termino_periodo_recebimento_proposta),
        qtd_produtos: item.qtd_produtos || 0,
        fornecedores: item.fornecedores ? item.fornecedores.length : 0,
        updated_at: formatDateTime(item.cotacao.updated_at),
        _original: item
      };
    });
  };

  const fetchCotacaoDetalhes = async (idCotacao) => {
    if (!idCotacao) {
      cotacaoAtual.value = null;
      return null;
    }

    const cacheKey = `cotacao_${idCotacao}`;

    // Verificar cache
    if (cache.has(cacheKey)) {
      const cached = cache.get(cacheKey);
      if (Date.now() - cached.timestamp < CACHE_TTL) {
        cotacaoAtual.value = cached.data;
        return cached.data;
      }
    }

    isLoading.value = true;

    try {
      const {data} = await api.get(`${API_ENDPOINTS.BUSCAR_DETALHES}?cotacao_transacao_id=${idCotacao}`);

      cotacaoAtual.value = data;

      // Atualizar cache
      cache.set(cacheKey, {
        data: data,
        timestamp: Date.now()
      });

      return data;
    } catch (error) {
      
      toast.error(ERROR_MESSAGES.LOAD_COTACAO);
      return null;
    } finally {
      isLoading.value = false;
    }
  };

  const updateCotacaoLocal = (idCotacao, updates) => {
    const index = cotacoes.value.findIndex(c => c.cotacao_transacao_id === idCotacao);
    if (index !== -1) {
      cotacoes.value[index] = {
        ...cotacoes.value[index],
        ...updates
      };
    }

    // Invalidar cache
    invalidateCache(`cotacao_${idCotacao}`);
  };

  const removeCotacaoLocal = (idCotacao) => {
    cotacoes.value = cotacoes.value.filter(c => c.cotacao_transacao_id !== idCotacao);

    // Invalidar cache
    invalidateCache(`cotacao_${idCotacao}`);
    invalidateAllListCache();
  };

  const addCotacaoLocal = (cotacao) => {
    const processed = processarCotacoes([{cotacao}]);
    cotacoes.value.unshift(processed[0]);

    // Invalidar cache de listagem
    invalidateAllListCache();
  };

  const refresh = async () => {
    invalidateAllListCache();
    await fetchCotacoes(false);
  };

  const refreshCotacao = async (idCotacao) => {
    invalidateCache(`cotacao_${idCotacao}`);
    return await fetchCotacaoDetalhes(idCotacao);
  };

  const invalidateCache = (key) => {
    cache.delete(key);
  };

  const invalidateAllListCache = () => {
    for (const key of cache.keys()) {
      if (key.startsWith('cotacoes_')) {
        cache.delete(key);
      }
    }
  };

  const clearCache = () => {
    cache.clear();
  };

  const sincronizarFornecedores = async () => {
    let ok = false;
    try {
      const response = await api.post(API_ENDPOINTS.SINCRONIZAR_FORNECEDORES);
      ok = !!response.data.success;
    } catch (error) {
      void error;
      ok = false;
    } finally {
      await refresh();
    }
    return ok;
  };

  const loadNextPage = async () => {
    if (scrollInfo.value.has_more && !scrollInfo.value.loading) {
      await fetchCotacoes(true);
    }
  };

  // Computed properties
  const hasCotacoes = computed(() => cotacoes.value.length > 0);
  const hasMore = computed(() => scrollInfo.value.has_more);
  const totalCotacoes = computed(() => scrollInfo.value.total || cotacoes.value.length);

  return {
    // Estado (readonly para evitar mutações diretas)
    cotacoes: readonly(cotacoes),
    cotacaoAtual: readonly(cotacaoAtual),
    isLoading: readonly(isLoading),
    scrollInfo: readonly(scrollInfo),

    // Computed
    hasCotacoes,
    hasMore,
    totalCotacoes,

    // Métodos de fetch
    fetchCotacoes,
    fetchCotacaoDetalhes,
    loadNextPage,

    // Métodos de atualização local
    updateCotacaoLocal,
    removeCotacaoLocal,
    addCotacaoLocal,

    // Métodos de refresh
    refresh,
    refreshCotacao,

    // Cache
    clearCache,

    // Sincronização
    sincronizarFornecedores
  };
}
